/* ========================================
   NUCLEAR SPECIALTY PAGES - INTERACTIVE CONTROLLER
   Recipe AJAX, Mystery Dial, 404 Canvas, About SVG
   Performance Optimized - With Proper Cleanup
   ======================================== */

class NuclearSpecialtyPages {
    constructor() {
        // Store listener references for cleanup
        this._listeners = [];
        this._intervals = [];
        this._animationFrameId = null;

        this.pageType = this.detectPageType();
        this.init();
        // SPA cleanup + re-init is owned by the module-level initNuclearSpecialty().
    }

    detectPageType() {
        if (document.querySelector('.nuclear-recipe-page')) return 'recipe';
        if (document.querySelector('.nuclear-mystery-page')) return 'mystery';
        if (document.querySelector('.nuclear-404-page')) return '404';
        if (document.querySelector('.nuclear-about-page')) return 'about';
        return null;
    }

    // Add listener with tracking for cleanup
    addListener(target, event, handler, options) {
        target.addEventListener(event, handler, options);
        this._listeners.push({ target, event, handler, options });
    }

    // Cleanup all listeners when navigating away
    cleanup() {
        this._listeners.forEach(({ target, event, handler, options }) => {
            target.removeEventListener(event, handler, options);
        });
        this._listeners = [];

        this._intervals.forEach(id => clearInterval(id));
        this._intervals = [];

        if (this._animationFrameId) {
            cancelAnimationFrame(this._animationFrameId);
            this._animationFrameId = null;
        }
    }

    init() {
        switch (this.pageType) {
            case 'recipe':
                this.initRecipePage();
                break;
            case 'mystery':
                this.initMysteryPage();
                break;
            case '404':
                this.init404Page();
                break;
            case 'about':
                this.initAboutPage();
                break;
        }
    }

    /* === 1. RECIPE PAGE (AJAX Add to Cart) - NOW USES CartManager STRICTLY === */
    /* #87 & #22 Fix: Logic unified. */
    initRecipePage() {
        const shopButtons = document.querySelectorAll('.shop-hack-btn');
        // No manual fetches. All handled via CartManager.

        shopButtons.forEach(btn => {
            const handler = (e) => {
                e.preventDefault();
                // #92 Fix: Consistency - Could migrate to data-action="add-to-cart" but maintaining specific logic for now via CartManager
                const variantId = btn.dataset.productId;

                if (!variantId) {
                    console.error('No variant ID');
                    return;
                }

                // Disable button
                btn.disabled = true;
                btn.textContent = 'Adding...';

                if (window.CartManager) {
                    CartManager.addItem(variantId, 1, {
                        onSuccess: () => {
                            btn.textContent = 'Added! ✓';
                            btn.style.background = '#22c55e';
                            setTimeout(() => {
                                btn.disabled = false;
                                btn.textContent = 'Shop This Hack';
                                btn.style.background = '';
                            }, 2000);
                        },
                        onError: () => {
                            btn.textContent = 'Error';
                            btn.disabled = false;
                            btn.style.background = '';
                        }
                    });
                }
            };

            // #88 Fix: Track listener
            this.addListener(btn, 'click', handler);
        });
    }

    // updateCartCount removed - uses CartManager events instead

    /* === 2. MYSTERY PAGE (Scroll-to-Rotate Dial) - Throttled === */
    initMysteryPage() {
        const dial = document.getElementById('rotatingDial');
        const vaultDoor = document.getElementById('vaultDoor');

        if (!dial) return;

        let lastScroll = 0;
        let rotation = 0;
        const targetSequence = [90, 180, 270];
        let ticking = false;

        const scrollHandler = () => {
            if (ticking) return;

            ticking = true;
            requestAnimationFrame(() => {
                const currentScroll = window.pageYOffset;
                const delta = currentScroll - lastScroll;

                rotation += delta * 0.5;
                rotation = rotation % 360;

                dial.setAttribute('transform', `rotate(${rotation} 200 200)`);
                this.checkDialAlignment(rotation, targetSequence);

                lastScroll = currentScroll;
                ticking = false;
            });
        };

        this.addListener(window, 'scroll', scrollHandler, { passive: true });
    }

    checkDialAlignment(rotation, targetSequence) {
        const tolerance = 10;
        const normalizedRotation = ((rotation % 360) + 360) % 360;

        const symbol1 = document.getElementById('symbol1');
        const symbol2 = document.getElementById('symbol2');
        const symbol3 = document.getElementById('symbol3');
        const vaultDoor = document.getElementById('vaultDoor');

        if (!symbol1 || !symbol2 || !symbol3 || !vaultDoor) return;

        if (Math.abs(normalizedRotation - targetSequence[0]) < tolerance) {
            symbol1.textContent = '🌶️';
            symbol1.classList.add('matched');
        }

        if (Math.abs(normalizedRotation - targetSequence[1]) < tolerance) {
            symbol2.textContent = '🧀';
            symbol2.classList.add('matched');
        }

        if (Math.abs(normalizedRotation - targetSequence[2]) < tolerance) {
            symbol3.textContent = '🧄';
            symbol3.classList.add('matched');
        }

        if (symbol1.classList.contains('matched') &&
            symbol2.classList.contains('matched') &&
            symbol3.classList.contains('matched')) {

            setTimeout(() => {
                vaultDoor.classList.add('open');
                // #82 Fix: Dependency check
                if (window.nuclear && window.nuclear.audio && window.nuclear.audio.click) {
                    // #40 Fix: Audio Safety catch
                    try {
                        window.nuclear.audio.click.play();
                    } catch (e) {
                        // Audio blocked or failed
                        console.warn('Audio play failed', e);
                    }
                }
            }, 500);
        }
    }

    /* === 3. 404 PAGE (Canvas Spill + Mouse Eraser) - OPTIMIZED === */
    init404Page() {
        const canvas = document.getElementById('spillCanvas');
        const homeBtn = document.getElementById('homeBtn');

        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        // REDUCED particle count for performance (was 3000)
        const particleCount = 500;
        const particles = [];

        for (let i = 0; i < particleCount; i++) {
            particles.push({
                x: Math.random() * canvas.width,
                y: Math.random() * canvas.height,
                radius: Math.random() * 4 + 2,
                color: `rgba(255, 215, 0, ${Math.random() * 0.5 + 0.3})`,
                visible: true
            });
        }

        const drawParticles = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];
                if (p.visible) {
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
                    ctx.fillStyle = p.color;
                    ctx.fill();
                }
            }
        };

        drawParticles();

        let isErasing = false;
        const eraserRadius = 60;
        let pendingDraw = false;

        // Use spatial grid for O(1) collision detection instead of O(n)
        const gridSize = eraserRadius * 2;
        const grid = {};

        particles.forEach((p, i) => {
            const cellX = Math.floor(p.x / gridSize);
            const cellY = Math.floor(p.y / gridSize);
            const key = `${cellX},${cellY}`;
            if (!grid[key]) grid[key] = [];
            grid[key].push(i);
        });

        const eraseNearMouse = (mouseX, mouseY) => {
            const cellX = Math.floor(mouseX / gridSize);
            const cellY = Math.floor(mouseY / gridSize);

            // Check only nearby cells
            for (let dx = -1; dx <= 1; dx++) {
                for (let dy = -1; dy <= 1; dy++) {
                    const key = `${cellX + dx},${cellY + dy}`;
                    const indices = grid[key];
                    if (!indices) continue;

                    for (const i of indices) {
                        const p = particles[i];
                        if (!p.visible) continue;

                        const distX = p.x - mouseX;
                        const distY = p.y - mouseY;
                        if (distX * distX + distY * distY < eraserRadius * eraserRadius) {
                            p.visible = false;
                        }
                    }
                }
            }
        };

        const checkCleanup = () => {
            let visibleCount = 0;
            // #84 Fix: Performance Optimization - break early if threshold met?
            // Actually, we need total visible. But we can optimize by not running this every frame if not needed.
            // Current is fine for reduced particle count (500).
            for (let i = 0; i < particles.length; i++) {
                if (particles[i].visible) visibleCount++;
            }
            if (visibleCount / particleCount < 0.1 && homeBtn) {
                homeBtn.classList.add('revealed');
                homeBtn.style.opacity = '1';
                homeBtn.style.pointerEvents = 'auto';
            }
        };

        const handleMove = (clientX, clientY) => {
            if (!isErasing) return;

            const rect = canvas.getBoundingClientRect();
            const mouseX = clientX - rect.left;
            const mouseY = clientY - rect.top;

            eraseNearMouse(mouseX, mouseY);

            if (!pendingDraw) {
                pendingDraw = true;
                this._animationFrameId = requestAnimationFrame(() => {
                    drawParticles();
                    checkCleanup();
                    pendingDraw = false;
                });
            }
        };

        this.addListener(canvas, 'mousedown', () => { isErasing = true; });
        this.addListener(canvas, 'mouseup', () => { isErasing = false; });
        this.addListener(canvas, 'mousemove', (e) => handleMove(e.clientX, e.clientY));

        this.addListener(canvas, 'touchstart', () => { isErasing = true; }, { passive: true });
        this.addListener(canvas, 'touchend', () => { isErasing = false; });
        this.addListener(canvas, 'touchmove', (e) => {
            const touch = e.touches[0];
            handleMove(touch.clientX, touch.clientY);
        }, { passive: true });

        // Debounced resize
        let resizeTimeout;
        this.addListener(window, 'resize', () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
                canvas.width = window.innerWidth;
                canvas.height = window.innerHeight;
                drawParticles();
            }, 150);
        });
    }

    /* === 4. ABOUT PAGE (SVG Path Drawing) - Throttled === */
    initAboutPage() {
        const path = document.getElementById('threadPath');
        const sections = document.querySelectorAll('.story-section');

        if (!path || sections.length === 0) return;

        const pathLength = path.getTotalLength();
        let ticking = false;

        const scrollHandler = () => {
            if (ticking) return;

            ticking = true;
            requestAnimationFrame(() => {
                const scrollY = window.pageYOffset;
                const docHeight = document.documentElement.scrollHeight - window.innerHeight;
                const scrollPercent = docHeight > 0 ? scrollY / docHeight : 0;

                const drawLength = pathLength * scrollPercent;
                path.style.strokeDashoffset = pathLength - drawLength;

                sections.forEach((section) => {
                    const rect = section.getBoundingClientRect();
                    const sectionCenter = rect.top + rect.height / 2;
                    const viewportCenter = window.innerHeight / 2;

                    if (Math.abs(sectionCenter - viewportCenter) < 300) {
                        const content = section.querySelector('.story-content');
                        if (content) content.classList.add('visible');
                    }
                });

                ticking = false;
            });
        };

        this.addListener(window, 'scroll', scrollHandler, { passive: true });
    }
}

// Initialize on DOM ready (and after SPA navigation)
function initNuclearSpecialty() {
    // Cleanup previous instance before re-creating
    if (window.nuclearSpecialty && typeof window.nuclearSpecialty.cleanup === 'function') {
        window.nuclearSpecialty.cleanup();
    }
    window.nuclearSpecialty = new NuclearSpecialtyPages();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initNuclearSpecialty);
} else {
    initNuclearSpecialty();
}

// Re-instantiate after SPA navigation (the constructor's own
// pageTransitionComplete listener only cleans up; it does not re-init)
window.addEventListener('pageTransitionComplete', initNuclearSpecialty);
